/**
 * Roll a number to its new value instead of swapping it.
 *
 * Used by the dashboard tiles so a refresh reads as the figure moving rather
 * than as the page flickering — and so the eye is drawn to whichever number
 * actually changed.
 *
 * Two things this has to get right:
 *
 *  - an animation interrupted mid-flight resumes from where it visually is,
 *    not from where the last one started. Otherwise a second update while the
 *    first is still running makes the number jump backwards before continuing.
 *  - prefers-reduced-motion is honoured. Numbers churning on a landing page is
 *    exactly the sort of motion people turn that setting on to stop.
 */

import { useEffect, useRef, useState } from 'react'

const DEFAULT_DURATION_MS = 900

/** Fast out of the gate, settling gently — an odometer, not a linear ramp. */
export function easeOutCubic(t) {
  const clamped = Math.min(1, Math.max(0, t))
  return 1 - Math.pow(1 - clamped, 3)
}

/** Where the value sits at `elapsed` into a `duration`-long run from → to. */
export function interpolate(from, to, elapsed, duration) {
  if (duration <= 0) return to
  return from + (to - from) * easeOutCubic(elapsed / duration)
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * @param {number} value    the number to settle on
 * @param {Object} [options]
 * @param {number} [options.duration] milliseconds; 0 disables the animation
 * @returns {number} the value to render this frame
 */
export function useAnimatedNumber(value, { duration = DEFAULT_DURATION_MS } = {}) {
  const target = Number.isFinite(value) ? value : 0
  const [display, setDisplay] = useState(0)

  // What is on screen right now. Read as the origin of the next run, so an
  // interrupted animation continues from the visible number.
  const displayRef = useRef(0)
  const frameRef = useRef(null)

  useEffect(() => {
    if (displayRef.current === target) return

    if (duration <= 0 || prefersReducedMotion()) {
      displayRef.current = target
      setDisplay(target)
      return
    }

    const from = displayRef.current
    let startedAt = null

    const step = (timestamp) => {
      if (startedAt === null) startedAt = timestamp
      const elapsed = timestamp - startedAt
      const next = interpolate(from, target, elapsed, duration)

      displayRef.current = next
      setDisplay(next)

      if (elapsed < duration) {
        frameRef.current = requestAnimationFrame(step)
      } else {
        // Land exactly on the target — easing leaves a rounding tail otherwise,
        // which shows up as a tile reading $591.00 while the truth is $591.
        displayRef.current = target
        setDisplay(target)
      }
    }

    frameRef.current = requestAnimationFrame(step)
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [target, duration])

  return display
}
